import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, UserPlus, KeyRound, Trash2 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import Pagination from "@/components/ui/PaginationWrapper";
import { userService } from '../../_services';
import * as signalR from '@aspnet/signalr';

function ManageEmail({
    titleText,
    wholesalerId,
    wholesalerName,
    dataId,
    activePagin,
    whSearch,
    openWholesalerPopup,
    closeDeleteUserPopup,
    closeResetPasswordPopup,
    closePopup
}) {
    const [loading, setLoading] = useState(true);
    const [userList, setUserList] = useState([]);
    const [userListTemp, setUserListTemp] = useState([]);
    const [mainAPIResult, setMainAPIResult] = useState([]);
    const [activePage, setActivePage] = useState(1);
    const [createNewUser, setCreateNewUser] = useState(false);
    const [newUserName, setNewUserName] = useState('');
    const [newUserEmail, setNewUserEmail] = useState('');
    const [userRole, setUserRole] = useState('Finance'); // Default to Finance
    const [userNameError, setUserNameError] = useState('');
    const [userEmailError, setUserEmailError] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [dataNotFound, setDataNotFound] = useState('');
    const [searchText, setSearchText] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const fetchDataLocally = useCallback(async (roleName, roleType, id) => {
        try {
            const apidata = await userService.GetDashboardList(roleName, roleType, id);
            
            if (apidata.length <= 12) {
                setUserList(apidata);
                setMainAPIResult(apidata);
                setUserListTemp(apidata);
            } else {
                let userlistobj = JSON.parse(JSON.stringify(apidata));
                let usertemplistobj = userlistobj.length > 12 ? userlistobj.splice(0, 12) : userlistobj;
                setUserList(apidata);
                setMainAPIResult(apidata);
                setUserListTemp(usertemplistobj);
            }
            setLoading(false);
        } catch (error) {

            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (userdata) {
            fetchDataLocally(userdata.paramsname, userdata.userrole, dataId);
        }
    }, [userdata, dataId, fetchDataLocally]);

    const handlePageChange = useCallback((pageNumber) => {
        let userlistobj = JSON.parse(JSON.stringify(userList));
        let usertemplistobj = userlistobj.length > 12 
            ? userlistobj.splice((pageNumber - 1) * 12, 12) 
            : userlistobj;
        setActivePage(pageNumber);
        setUserListTemp(usertemplistobj);
    }, [userList]);

    const handleSearch = useCallback(() => {
        let filteredList = mainAPIResult.filter(e1 =>
            e1.userName.toLowerCase().indexOf(searchText.toLowerCase()) !== -1 ||
            e1.email.toLowerCase().indexOf(searchText.toLowerCase()) !== -1
        );

        if (filteredList.length === 0) {
            setDataNotFound('No records found');
        } else {
            setDataNotFound('');
        }

        let usertemplistobj = filteredList.length > 12 ? filteredList.splice(0, 12) : filteredList;
        setActivePage(1);
        setUserList(filteredList);
        setUserListTemp(usertemplistobj);
    }, [mainAPIResult, searchText]);

    const addUser = () => {
        setCreateNewUser(true);
        setNewUserName('');
        setNewUserEmail('');
        setUserRole('Finance');
        setUserNameError('');
        setUserEmailError('');
        setError('');
    };

    const saveData = useCallback(async () => {
        setUserNameError('');
        setUserEmailError('');
        setError('');
        setSuccessMessage('');

        // Validate username
        if (!newUserName || newUserName === '' || newUserName === 'User Name') {
            setUserNameError('User Name cannot be blank');
            return;
        }

        if (userService.ValidateSpace(newUserName)) {
            setUserNameError('Space not acceptable');
            return;
        }

        // Validate email
        if (!newUserEmail || newUserEmail === '' || newUserEmail === 'Email') {
            setUserEmailError('Email ID cannot be blank');
            return;
        }

        if (!userService.ValidateEmail(newUserEmail)) {
            setUserEmailError('Enter a valid email ID');
            return;
        }

        setIsSaving(true);

        try {
            // Call AddWhDashUser to create new user (not UpdateFinanceUser!)
            const apidata = await userService.AddWhDashUser(
                userdata.paramsname,
                userdata.userrole,
                wholesalerName,
                String(wholesalerId), 
                newUserName,
                newUserEmail
            );

            if (apidata && apidata.length > 0) {
                if (apidata[0].status_code == "1") {
                    // Add new user to the list
                    const newUser = {
                        userName: newUserName,
                        email: newUserEmail,
                        isfu: false,
                        istechnical: false,
                        issupport: false
                    };

                    const updatedList = [...userList, newUser];

                    setUserList(updatedList);
                    setMainAPIResult(updatedList);
                    setUserListTemp(updatedList.slice(0, 12));
                    setSuccessMessage(apidata[0].status_message);
                    setCreateNewUser(false);
                    setNewUserName('');
                    setNewUserEmail('');
                    setIsSaving(false);

                    // Clear success message after 2 seconds
                    setTimeout(() => {
                        setSuccessMessage('');
                    }, 2000);
                } else {
                    setError(apidata[0].status_message);
                    setIsSaving(false);
                }
            } else {
                setError('Error! Please try again.');
                setIsSaving(false);
            }
        } catch (error) {
            console.error('Error creating user:', error);
            setError('Error! Please try again.');
            setIsSaving(false);
        }
    }, [newUserName, newUserEmail, userdata, wholesalerId, wholesalerName, userList]);

    const cancelData = () => {
        setCreateNewUser(false);
        setUserNameError('');
        setUserEmailError('');
    };

    const handleBack = () => {
        // Just close ManageEmail, ManageWholesaler is already open underneath
        closePopup(dataId, wholesalerId, wholesalerName, activePagin, whSearch);
    };

    const handleChangePassword = (user) => {
        closePopup(dataId, wholesalerId, wholesalerName, activePagin, whSearch);
        closeResetPasswordPopup(wholesalerName, user.userName, user.email, activePagin, whSearch);
    };

    const handleDeleteUser = (user) => {
        closePopup(dataId, wholesalerId, wholesalerName, activePagin, whSearch);
        closeDeleteUserPopup(user.userName, user.email, activePagin, whSearch);
    };

    const handleRoleChange = useCallback(async (user, roleType, index) => {

        try {
            // Determine the new role states
            const isFinance = roleType === 'finance';
            const isTechnical = roleType === 'technical';
            const isSupport = roleType === 'support';

            // Call API to update user role
            const apidata = await userService.UpdateFinanceUser(
                user.userName,
                user.email,
                wholesalerId,
                isFinance,
                isSupport,
                isTechnical
            );

            if (apidata && apidata.length > 0) {
                if (apidata[0].status_code == "1") {

                    setSuccessMessage('Role updated successfully');
                    setError('');
                    
                    // Refresh data from server
                    await fetchDataLocally(userdata.paramsname, userdata.userrole, dataId);
                    
                    // SignalR notification
                    try {
                        const connection = new signalR.HubConnectionBuilder()
                            .withUrl("/notification", {
                                skipNegotiation: true,
                                transport: signalR.HttpTransportType.WebSockets
                            })
                            .build();
                        
                        await connection.start();
                        await connection.invoke("UpdateFinanceUser", wholesalerId, user.email);
                    } catch (err) {

                    }
                    
                    setTimeout(() => setSuccessMessage(''), 3000);
                } else {

                    setError('Failed to update role');
                    setSuccessMessage('');
                    // If API failed, refresh to revert
                    await fetchDataLocally(userdata.paramsname, userdata.userrole, dataId);
                }
            }
        } catch (error) {

            // Revert on error
            await fetchDataLocally(userdata.paramsname, userdata.userrole, dataId);
        }
    }, [wholesalerId, dataId, userdata.paramsname, userdata.userrole, fetchDataLocally]);

    return (
        <Dialog open={true} onOpenChange={handleBack}>
            <DialogContent className="max-w-6xl h-[calc(100vh-3rem)]">
                <div className="flex flex-col h-full">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>Manage Emails: {wholesalerName}</DialogTitle>
                    </DialogHeader>

                    {loading ? (
                        <div className="flex-1 flex justify-center items-center">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : (
                        <>
                            {/* Fixed Controls */}
                            <div className="flex-shrink-0 space-y-4">
                                <div className="flex gap-2 flex-wrap">
                                    <Button onClick={addUser} className="bg-primary hover:bg-primary/90">
                                        <UserPlus className="h-4 w-4 mr-2" />
                                        Add User
                                    </Button>
                                </div>

                                <div className="flex gap-2">
                                    <Input
                                        type="text"
                                        placeholder="Search"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        onKeyUp={handleSearch}
                                        className="flex-1"
                                    />
                                </div>

                                {createNewUser && (
                                    <div className="border rounded-lg p-4 bg-muted/30">
                                        <div className="flex gap-4 items-start">
                                            <div className="flex-1">
                                                <Input
                                                    type="text"
                                                    placeholder="User Name"
                                                    value={newUserName}
                                                    onChange={(e) => {
                                                        setNewUserName(e.target.value);
                                                        setUserNameError('');
                                                    }}
                                                />
                                                {userNameError && (
                                                    <p className="text-sm text-destructive mt-1">{userNameError}</p>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <Input
                                                    type="email"
                                                    placeholder="Email"
                                                    value={newUserEmail}
                                                    onChange={(e) => {
                                                        setNewUserEmail(e.target.value);
                                                        setUserEmailError('');
                                                    }}
                                                />
                                                {userEmailError && (
                                                    <p className="text-sm text-destructive mt-1">{userEmailError}</p>
                                                )}
                                            </div>
                                            <div className="flex gap-2">
                                                <Button onClick={saveData} disabled={isSaving}>
                                                    {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                                    Save
                                                </Button>
                                                <Button variant="secondary" onClick={cancelData} disabled={isSaving}>
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Scrollable Table */}
                            {userList && userList.length > 0 ? (
                                <>
                                    <div className="flex-1 overflow-y-auto min-h-0 mt-4">
                                        <div className="border rounded-lg overflow-hidden">
                                            <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>User Name</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead className="text-center">Finance</TableHead>
                                                <TableHead className="text-center">Technical</TableHead>
                                                <TableHead className="text-center">Support</TableHead>
                                                <TableHead className="w-32">Action</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {userListTemp.map((user, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{user.userName}</TableCell>
                                                    <TableCell>{user.email}</TableCell>
                                    <TableCell className="text-center">
                                        <div className="inline-flex items-center">
                                            <Checkbox
                                                checked={user.isfu === true}
                                                onCheckedChange={() => handleRoleChange(user, 'finance', index)}
                                                className="cursor-pointer"
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <div className="inline-flex items-center">
                                            <Checkbox
                                                checked={user.istechnical === true}
                                                onCheckedChange={() => handleRoleChange(user, 'technical', index)}
                                                className="cursor-pointer"
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <div className="inline-flex items-center">
                                            <Checkbox
                                                checked={user.issupport === true}
                                                onCheckedChange={() => handleRoleChange(user, 'support', index)}
                                                className="cursor-pointer"
                                            />
                                        </div>
                                    </TableCell>
                                                    <TableCell className="w-32">
                                                        <TooltipProvider>
                                                            <div className="flex gap-2">
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => handleChangePassword(user)}
                                                                        >
                                                                            <KeyRound className="h-4 w-4" />
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>Change Password</TooltipContent>
                                                                </Tooltip>
                                                                {user.userName !== wholesalerName && 
                                                                 !user.isfu && 
                                                                 !user.istechnical && 
                                                                 !user.issupport && (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            onClick={() => handleDeleteUser(user)}
                                                                        >
                                                                            <Trash2 className="h-4 w-4" />
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>Delete User</TooltipContent>
                                                                </Tooltip>
                                                                )}
                                                            </div>
                                                        </TooltipProvider>
                                                    </TableCell>
                                                </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        </div>

                                        {/* Fixed Pagination */}
                                        {userList.length > 12 && (
                                            <div className="flex-shrink-0 flex justify-center mt-4">
                                                <Pagination
                                                    activePage={activePage}
                                                    itemsCountPerPage={12}
                                                    totalItemsCount={userList.length}
                                                    pageRangeDisplayed={5}
                                                    onChange={handlePageChange}
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <h3>{dataNotFound || 'No users found'}</h3>
                                    </div>
                                )}

                            {/* Messages */}
                            {successMessage && (
                                <div className="flex-shrink-0 text-sm text-green-700 bg-green-50 p-3 rounded-md border border-green-200 mt-4">
                                    {successMessage}
                                </div>
                            )}
                            
                            {error && (
                                <div className="flex-shrink-0 text-sm text-destructive bg-destructive/10 p-3 rounded-md mt-4">
                                    {error}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default ManageEmail;
